import { useCallback, useEffect, useState } from 'react'
import { StatusBadge, colors } from '../../components/ui'
import { api } from '../../services/api'
import { relativeTime } from './fleet-helpers'
import type { DeviceSnapshot } from './DeviceDetailModal'

type ActivityItem =
  | {
      occurredAt: string
      id: string
      source: 'error'
      errorType: string
      message: string
      udmVersion: string | null
    }
  | {
      occurredAt: string
      id: string
      source: 'result'
      testCode: string | null
      testName: string | null
      rawValue: string | null
      unit: string | null
      disposition: string
    }

interface ActivityResponse {
  readonly items: readonly ActivityItem[]
  readonly nextCursor: string | null
}

function errorBadgeStatus(errorType: string): string {
  switch (errorType) {
    case 'comm_failure':
      return 'critical'
    case 'config_error':
      return 'warning'
    default:
      return 'info'
  }
}

const fieldRow: React.CSSProperties = {
  display: 'flex',
  padding: '0.5rem 0',
  borderBottom: `1px solid ${colors.border}`,
  fontSize: '13px',
}
const fieldLabel: React.CSSProperties = { width: '140px', fontWeight: 500, color: colors.textMuted, flexShrink: 0 }

export function DeviceActivityTab({ device }: { device: DeviceSnapshot }) {
  const [showErrors, setShowErrors] = useState(true)
  const [showResults, setShowResults] = useState(true)
  const [items, setItems] = useState<readonly ActivityItem[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Derived only from stable toggle state — NEVER Date.now() (see PR #291 loop).
  const sources = [showErrors ? 'errors' : null, showResults ? 'results' : null].filter(Boolean).join(',')

  const load = useCallback(
    async (cursor: string | null, append: boolean) => {
      if (!device.siteId) {
        setError('This device has no site; activity is unavailable.')
        return
      }
      setLoading(true)
      setError(null)
      const params = new URLSearchParams({ siteId: device.siteId, sources, limit: '25' })
      if (cursor) params.set('cursor', cursor)
      try {
        const res = await api.get<ActivityResponse>(`/fleet/devices/${device.deviceId}/activity?${params.toString()}`)
        const data = res.data ?? { items: [], nextCursor: null }
        setItems((prev) => (append ? [...prev, ...data.items] : [...data.items]))
        setNextCursor(data.nextCursor)
      } catch {
        setError('Could not load activity.')
      } finally {
        setLoading(false)
      }
    },
    [device.deviceId, device.siteId, sources],
  )

  // Reset + reload whenever the source filter changes. `load` is stable per
  // (deviceId, siteId, sources); no time-derived deps, so this runs once per
  // toggle, not in a loop.
  useEffect(() => {
    if (!sources) return // at least one toggle must stay on
    setItems([])
    setNextCursor(null)
    void load(null, false)
  }, [load, sources])

  return (
    <div>
      {(
        [
          ['Device', device.deviceName],
          ['Status', device.status],
          ['UDM Version', device.udmVersion ?? '—'],
          ['Last Heartbeat', relativeTime(device.lastHeartbeatAt)],
          ['Errors (24h)', String(device.errorCountLast24h)],
        ] as const
      ).map(([label, value]) => (
        <div key={label} style={fieldRow}>
          <span style={fieldLabel}>{label}</span>
          <span>{value}</span>
        </div>
      ))}

      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', margin: '0.75rem 0' }}>
        <label style={{ fontSize: '13px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={showErrors}
            onChange={() => setShowErrors((v) => (showResults ? !v : v))}
            aria-label="Errors"
          />{' '}
          Errors
        </label>
        <label style={{ fontSize: '13px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={showResults}
            onChange={() => setShowResults((v) => (showErrors ? !v : v))}
            aria-label="Results"
          />{' '}
          Results
        </label>
      </div>

      {error && <div style={{ color: colors.danger, fontSize: '13px', marginBottom: '0.5rem' }}>{error}</div>}

      {items.length === 0 && !loading && !error && (
        <div style={{ color: colors.textMuted, fontSize: '13px' }}>No activity for this device.</div>
      )}

      {items.map((item) => (
        <div key={`${item.source}-${item.id}`} style={fieldRow}>
          <span style={{ ...fieldLabel, width: '160px' }}>{new Date(item.occurredAt).toLocaleString()}</span>
          {item.source === 'error' ? (
            <span style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <StatusBadge status={errorBadgeStatus(item.errorType)} label={item.errorType} />
              <span>{item.message}</span>
            </span>
          ) : (
            <span style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <strong>{item.testCode ?? '—'}</strong>
              <span>
                {item.rawValue ?? '—'} {item.unit ?? ''}
              </span>
              <StatusBadge status="info" label={item.disposition} />
            </span>
          )}
        </div>
      ))}

      {nextCursor && (
        <button
          type="button"
          disabled={loading}
          onClick={() => void load(nextCursor, true)}
          style={{
            marginTop: '0.75rem',
            padding: '0.35rem 0.75rem',
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            background: '#fff',
            cursor: 'pointer',
            fontSize: '12px',
          }}
        >
          {loading ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  )
}
